import { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  X, Sparkle, CircleNotch, CaretRight, CaretLeft,
  Lightning, MagnifyingGlass, Microscope, Check, CheckCircle, LockSimple,
} from '@phosphor-icons/react'
import { api } from '@/lib/api'
import { getAccessToken } from '@/lib/tokenStorage'
import { useScrollLock } from '@/lib/useScrollLock'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useContactStore } from '@/stores/contactStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { FieldSelectionPanel, GROUP_ICONS } from './FieldSelectionPanel'
import { SourcePill } from './SourceBadges'
import { explainEnrichmentRunError } from '@/lib/enrichmentError'
import { StepIndicator } from '@/components/ui/StepIndicator'
import {
  FIELD_CATALOG,
  getDefaultSelectedFields,
  resolveProviders,
  type FieldGroup,
} from './enrichmentFields'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DepthOption {
  id: string
  label: string
  description: string
  est_seconds: number
}

interface IntegrationOption {
  id: string
  name: string
  configured: boolean
  /** True when the account's plan tier does not include this data source.
   *  Separate from `configured` (which is only about having a key) because
   *  the two need different fixes: connect a key vs upgrade a plan. The
   *  orchestrator skips a plan-locked provider whatever the UI sends. */
  plan_locked?: boolean
}

interface EnrichmentConfig {
  depths: DepthOption[]
  integrations: IntegrationOption[]
  has_ai_keys: boolean
  default_depth: string
}

// Depth → visible field groups
const DEPTH_GROUPS: Record<string, FieldGroup[]> = {
  quick:    ['person'],
  standard: ['person', 'company'],
  deep:     ['person', 'company', 'financial', 'news'],
}

// Depth → data source rules: quick is AI-only (no sources), standard caps at 2,
// deep is unlimited.
const MAX_STANDARD_SOURCES = 2

// Enrichment sources the user can actually connect in Settings → Integrations
// ("news" ↔ the NewsAPI.org card). The backend supports more providers
// (RocketReach, Cognism, Lusha, UpLead, Snov) but they have no Settings card,
// so they are neither shown nor counted as "more available" here. SEC EDGAR
// needs no key: it always runs (except quick lookup) and is not shown as a
// chip, like the built-in AI.
const SETTINGS_CONNECTABLE = new Set([
  'apollo', 'hunter', 'peopledatalabs', 'explorium',
  'fmp', 'alpha_vantage', 'news',
])

const DEPTH_ICONS: Record<string, React.ReactNode> = {
  quick:    <Lightning className="h-5 w-5" />,
  standard: <MagnifyingGlass className="h-5 w-5" />,
  deep:     <Microscope className="h-5 w-5" />,
}

// ---------------------------------------------------------------------------
// Main Modal — 3-step stepper
// ---------------------------------------------------------------------------

interface EnrichmentCatalogModalProps {
  isOpen: boolean
  onClose: () => void
  contactIds: number[]
  mode: 'bulk' | 'single'
  onEnrichmentComplete?: (results: any) => void
  onEnrichmentStarted?: () => void
}

export function EnrichmentCatalogModal({
  isOpen, onClose, contactIds, mode, onEnrichmentComplete, onEnrichmentStarted,
}: EnrichmentCatalogModalProps) {
  useScrollLock(isOpen)
  const navigate = useNavigate()
  // Step state
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)

  // Step 1: depth + data sources
  const [config, setConfig] = useState<EnrichmentConfig | null>(null)
  const [selectedDepth, setSelectedDepth] = useState('standard')
  const [enabledIntegrations, setEnabledIntegrations] = useState<Set<string>>(new Set())

  // Step 2: field selection
  const visibleGroups = useMemo(() => DEPTH_GROUPS[selectedDepth] || ['person', 'company'], [selectedDepth])
  const [selectedFields, setSelectedFields] = useState<Set<string>>(() => getDefaultSelectedFields(visibleGroups))

  // Step 4: progress
  const [progressSteps, setProgressSteps] = useState<Array<{ step: string; label: string }>>([])
  const [progressDone, setProgressDone] = useState(false)
  const [progressError, setProgressError] = useState<string | null>(null)
  // Bulk enrichment progress (real per-contact status, polled from the server).
  const [bulkProgress, setBulkProgress] = useState<{ done: number; completed: number; failed: number; total: number } | null>(null)
  // Set when the modal closes so an in-flight status poll stops touching state.
  const cancelPollRef = useRef(false)

  // Derived
  const resolvedProviders = useMemo(() => resolveProviders(selectedFields), [selectedFields])
  // Only surface integrations the user can manage in the Settings UI.
  const visibleIntegrations = useMemo(
    () => (config?.integrations || []).filter(i => SETTINGS_CONNECTABLE.has(i.id)),
    [config]
  )
  // Providers that will actually run: the configured data sources enabled on
  // Step 1 — mirrors what handleRun sends, in config display order. Quick
  // lookup is AI-only, so no data sources run.
  const runProviders = useMemo(
    () => selectedDepth === 'quick'
      ? []
      : visibleIntegrations.filter(i => enabledIntegrations.has(i.id) && !i.plan_locked),
    [visibleIntegrations, enabledIntegrations, selectedDepth]
  )

  // Stop any in-flight bulk status poll when the modal closes/unmounts.
  useEffect(() => {
    if (isOpen) cancelPollRef.current = false
    return () => { cancelPollRef.current = true }
  }, [isOpen])

  // Fetch config on open
  useEffect(() => {
    if (!isOpen) return
    setStep(1)
    setLoading(false)
    setConfig(null)
    setBulkProgress(null)
    api.get('/enrichment/config').then(res => {
      const data = res.data as EnrichmentConfig
      setConfig(data)
      const depth = data.default_depth || 'standard'
      setSelectedDepth(depth)
      const configuredIds = data.integrations
        .filter(i => i.configured && !i.plan_locked && SETTINGS_CONNECTABLE.has(i.id))
        .map(i => i.id)
      setEnabledIntegrations(new Set(
        depth === 'standard' ? configuredIds.slice(0, MAX_STANDARD_SOURCES) : configuredIds
      ))
    }).catch(() => {
      toast.error('Failed to load enrichment config')
    })
  }, [isOpen])

  // Reset fields when depth changes; enforce the standard-depth source cap
  useEffect(() => {
    setSelectedFields(getDefaultSelectedFields(visibleGroups))
    if (selectedDepth === 'standard') {
      setEnabledIntegrations(prev =>
        prev.size > MAX_STANDARD_SOURCES
          ? new Set([...prev].slice(0, MAX_STANDARD_SOURCES))
          : prev
      )
    }
  }, [selectedDepth])

  const toggleIntegration = (id: string) => {
    setEnabledIntegrations(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        if (selectedDepth === 'standard' && next.size >= MAX_STANDARD_SOURCES) return prev
        next.add(id)
      }
      return next
    })
  }

  // ---------------------------------------------------------------------------
  // Run handler
  // ---------------------------------------------------------------------------

  const handleRun = async () => {
    setLoading(true)
    setProgressSteps([])
    setProgressDone(false)
    setProgressError(null)
    setStep(4) // switch to progress view

    try {
      // Chips on Step 1 are the source of truth for WHICH clients to call
      // (none for quick lookup — runProviders is already depth-aware).
      // Selected fields on Step 2 provide the per-provider config (enrich_person,
      // enrich_company, find_email, etc.). If a chip is enabled but no field
      // resolves to it (e.g. RocketReach has no field entry), still include the
      // provider with empty config so the runner uses its own defaults.
      const resolvedByName = new Map(resolvedProviders.map(p => [p.name, p]))
      const integrationProviders = runProviders.map(i =>
        resolvedByName.get(i.id) || { name: i.id, config: {} }
      )
      // SEC EDGAR needs no key — always include it (hidden from the chips),
      // except for quick lookup which is AI-only.
      if (selectedDepth !== 'quick') {
        integrationProviders.push(resolvedByName.get('sec_edgar') || { name: 'sec_edgar', config: {} })
      }

      const providers: any[] = [
        { name: 'native', config: { enabled_blocks: [] } },
        ...integrationProviders,
      ]

      if (mode === 'bulk') {
        const startRes = await api.post('/enrichment/bulk', {
          contact_ids: contactIds,
          providers,
          execution_mode: 'parallel',
          depth: selectedDepth,
        })
        // Contacts the server refused to enrich (flagged invalid / fake_email)
        // so as not to waste credits (bug 21670).
        const skipped = startRes.data?.skipped_invalid || 0
        // Let the parent know the bulk action fired so it can clear the
        // selection when the modal is closed (clearing now would unmount this
        // modal, since the bulk bar only renders while a selection exists).
        onEnrichmentStarted?.()

        const total = contactIds.length

        // All selected were invalid → nothing to enrich; report and stop.
        if (skipped >= total) {
          setBulkProgress({ done: total, completed: 0, failed: 0, total })
          setProgressDone(true)
          setLoading(false)
          useContactStore.getState().fetchContacts()
          toast.warning(`All ${total} skipped: invalid email`)
          return
        }

        // Poll REAL per-contact status until the batch settles — never report
        // "complete" until the server says so. Lightweight: one small query
        // every few seconds, capped, and cancelled if the modal closes. A
        // contact is "settled" once it's no longer in_progress, so skipped
        // invalid contacts don't stall the loop.
        setBulkProgress({ done: 0, completed: 0, failed: 0, total })
        const tally = async () => {
          const res = await api.post('/enrichment/statuses', { contact_ids: contactIds })
          const vals = Object.values((res.data?.statuses || {}) as Record<string, string>)
          const inProgress = vals.filter(s => s === 'in_progress').length
          const completed = vals.filter(s => s === 'completed').length
          const failed = vals.filter(s => s === 'failed').length
          return { done: total - inProgress, completed, failed, total }
        }
        const deadline = Date.now() + 15 * 60 * 1000 // safety cap
        let p = { done: 0, completed: 0, failed: 0, total }
        while (!cancelPollRef.current && Date.now() < deadline) {
          await new Promise(r => setTimeout(r, 4000))
          if (cancelPollRef.current) return
          try { p = await tally() } catch { continue }
          setBulkProgress(p)
          useContactStore.getState().fetchContacts()
          if (p.done >= total) break
        }
        if (cancelPollRef.current) return
        setProgressDone(true)
        setLoading(false)
        useContactStore.getState().fetchContacts()
        const skipNote = skipped ? ` (${skipped} skipped — invalid)` : ''
        const enrichable = total - skipped
        if (p.completed >= enrichable && p.failed === 0) toast.success(`Enriched ${p.completed} of ${enrichable}${skipNote}`)
        else if (p.completed > 0) toast.warning(`Enriched ${p.completed} of ${enrichable}; ${p.failed} failed${skipNote}`)
        else toast.error(`Enrichment failed for all ${enrichable} contacts${skipNote}`)
        return
      }

      // Single contact — trigger then stream progress
      await api.post(`/enrichment/${contactIds[0]}`, {
        providers,
        execution_mode: 'parallel',
        depth: selectedDepth,
      })

      // Notify parent that enrichment is in progress (so side panel can show status)
      onEnrichmentStarted?.()

      // Connect to SSE stream
      const token = getAccessToken()
      const baseUrl = import.meta.env.VITE_API_URL || '/api'
      const response = await fetch(`${baseUrl}/enrichment/${contactIds[0]}/stream`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!response.ok || !response.body) {
        throw new Error('Failed to connect to progress stream')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.type === 'step') {
              setProgressSteps(prev => {
                if (data.step === 'waiting' && prev.some(s => s.step === 'waiting')) return prev
                return [...prev, { step: data.step, label: data.label }]
              })
            } else if (data.type === 'complete') {
              if (onEnrichmentComplete) onEnrichmentComplete({ enrichment_data: data.enrichment_data, enrichment_results: data.enrichment_results || {} })
              toast.success('Enrichment complete!')
              setProgressDone(true)
              setLoading(false)
              useContactStore.getState().fetchContacts()
              return
            } else if (data.type === 'error') {
              setProgressError(data.error || 'Enrichment failed')
              setLoading(false)
              return
            }
          } catch { /* ignore parse errors */ }
        }
      }

      // Stream ended without explicit complete
      setProgressDone(true)
      setLoading(false)
      useContactStore.getState().fetchContacts()
    } catch (error: any) {
      setProgressError(error.response?.data?.detail || error.message || 'Enrichment failed')
      setLoading(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const STEPS = [
    { key: 1, label: 'Research Type' },
    { key: 2, label: 'Select Fields' },
    { key: 3, label: 'Confirm & Run' },
  ]

  if (typeof document === 'undefined') return null

  // Portalled to <body> because one of the call sites (ContactDetailModal)
  // renders this from inside a framer-motion slide-in drawer: a transformed
  // ancestor becomes the containing block for `position: fixed` descendants,
  // so an in-tree `fixed inset-0` would size and position itself against the
  // drawer instead of the viewport. AnimatePresence stays inside the portal
  // wrapping the conditional children so exit variants still run.
  return createPortal((
    <AnimatePresence>
      {isOpen && (
        <>
          {/* z-[100] is this folder's modal band (CrmImport/CrmExport/Zoho/
              BookMeeting/ComposeEmail): now that it's portalled, this is a
              sibling of the z-[70] contact drawer that opens it rather than a
              child, so the old z-50 would have rendered behind that drawer. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 backdrop-blur-md z-[100]"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            onClick={e => e.stopPropagation()}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="enrichment-catalog-title"
              className="bg-card border border-border/60 rounded-xl shadow-lg w-full max-w-2xl h-[560px] flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
                <div>
                  <h2 id="enrichment-catalog-title" className="text-lg font-emphasis tracking-tight text-foreground flex items-center gap-2">
                    <Sparkle className="w-5 h-5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                    Enrich Contact{contactIds.length > 1 ? 's' : ''}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {contactIds.length} contact{contactIds.length > 1 ? 's' : ''} selected
                  </p>
                </div>
                <button onClick={onClose} className="p-1.5 hover:bg-accent/30 rounded-lg transition-all">
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {/* Stepper indicator */}
              <div className="px-5 py-3 border-b border-border/50">
                <StepIndicator
                  steps={STEPS}
                  current={step}
                  onStepChange={(k) => setStep(Number(k))}
                  showNumbers
                  className="justify-center gap-2"
                />
              </div>

              {/* Step content */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <AnimatePresence mode="wait">
                  {/* Step 1: Research Type */}
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.15 }}
                      className="space-y-5"
                    >
                      {!config ? (
                        <div className="flex items-center justify-center py-12">
                          <CircleNotch className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : !config.has_ai_keys ? (
                        <div className="border border-yellow-500/20 rounded-xl p-6 bg-yellow-500/5 text-center">
                          <p className="text-sm font-medium text-foreground">No AI API Keys Configured</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Add your OpenAI or Anthropic API key in Settings to enable AI research.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div>
                            <p className="text-xs font-emphasis text-muted-foreground uppercase tracking-wider mb-3">
                              How thorough?
                            </p>
                            <div className="grid grid-cols-3 gap-3">
                              {config.depths.map(depth => (
                                <button
                                  key={depth.id}
                                  onClick={() => setSelectedDepth(depth.id)}
                                  className={cn(
                                    'flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center',
                                    selectedDepth === depth.id
                                      ? 'border-[#5e6ad2] bg-[#5e6ad2]/5 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.10] dark:text-[#a5b4fc]'
                                      : 'border-border/50 bg-background text-muted-foreground hover:border-border hover:text-foreground'
                                  )}
                                >
                                  <div className={cn(
                                    'p-2.5 rounded-lg',
                                    selectedDepth === depth.id ? 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.14]' : 'bg-muted/50'
                                  )}>
                                    {DEPTH_ICONS[depth.id] || <MagnifyingGlass className="h-5 w-5" />}
                                  </div>
                                  <span className="text-sm font-emphasis">{depth.label}</span>
                                  <span className="text-[10px] leading-tight">{depth.description}</span>
                                  <span className="text-[10px] text-muted-foreground">~{depth.est_seconds}s</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Data sources — hidden for quick lookup (AI-only) */}
                          {selectedDepth !== 'quick' && (
                            <div>
                              <p className="text-xs font-emphasis text-muted-foreground uppercase tracking-wider mb-2">
                                Your Data Sources ({visibleIntegrations.filter(i => i.configured).length} of {visibleIntegrations.length} connected)
                                {selectedDepth === 'standard' && (
                                  <span className="ml-1.5 normal-case tracking-normal font-normal">
                                    · select up to {MAX_STANDARD_SOURCES}
                                  </span>
                                )}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {visibleIntegrations.filter(i => i.configured).map(integration => {
                                  const planLocked = !!integration.plan_locked
                                  const enabled = !planLocked && enabledIntegrations.has(integration.id)
                                  // Two different reasons a pill can be
                                  // unclickable: the standard-depth cap is
                                  // full, or the plan does not include this
                                  // source. Both disable it; only the second
                                  // is a plan problem, so it says so.
                                  const capped = !enabled
                                    && selectedDepth === 'standard'
                                    && enabledIntegrations.size >= MAX_STANDARD_SOURCES
                                  const locked = planLocked || capped
                                  return (
                                    <button
                                      key={integration.id}
                                      onClick={() => toggleIntegration(integration.id)}
                                      disabled={locked}
                                      title={planLocked ? 'Not included in your plan — compare plans in Billing' : undefined}
                                      className={cn(
                                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                                        enabled
                                          ? 'border-[#5e6ad2]/40 bg-[#5e6ad2]/5 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.10] dark:text-[#a5b4fc]'
                                          : 'border-border/60 bg-muted/30 text-muted-foreground',
                                        locked && 'opacity-40 cursor-not-allowed'
                                      )}
                                    >
                                      {planLocked
                                        ? <LockSimple className="w-3 h-3" />
                                        : enabled ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                                      {integration.name}
                                    </button>
                                  )
                                })}
                              </div>
                              {visibleIntegrations.some(i => !i.configured) && (
                                <p className="text-[10px] text-muted-foreground mt-2">
                                  {visibleIntegrations.filter(i => !i.configured).length} more available — connect in{' '}
                                  <button
                                    onClick={() => {
                                      useSettingsStore.getState().setActiveSection('integrations')
                                      navigate('/settings')
                                    }}
                                    className="text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline"
                                  >
                                    Settings
                                  </button>
                                </p>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </motion.div>
                  )}

                  {/* Step 2: Field Selection */}
                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.15 }}
                    >
                      <p className="text-xs font-emphasis text-muted-foreground uppercase tracking-wider mb-3">
                        Select data fields to enrich
                      </p>
                      <FieldSelectionPanel
                        visibleGroups={visibleGroups}
                        selectedFields={selectedFields}
                        onSelectedFieldsChange={setSelectedFields}
                      />
                    </motion.div>
                  )}

                  {/* Step 3: Summary */}
                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.15 }}
                      className="space-y-5"
                    >
                      {/* Summary cards */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-xl border border-border/40 bg-muted/20 p-4 text-center">
                          <div className="flex items-center justify-center mb-2">
                            {DEPTH_ICONS[selectedDepth]}
                          </div>
                          <p className="text-sm font-emphasis text-foreground">
                            {config?.depths.find(d => d.id === selectedDepth)?.label || selectedDepth}
                          </p>
                          <p className="text-[10px] text-muted-foreground">Research depth</p>
                        </div>
                        <div className="rounded-xl border border-border/40 bg-muted/20 p-4 text-center">
                          <p className="text-2xl font-emphasis text-foreground">{selectedFields.size}</p>
                          <p className="text-[10px] text-muted-foreground">Fields selected</p>
                        </div>
                        <div className="rounded-xl border border-border/40 bg-muted/20 p-4 text-center">
                          <p className="text-2xl font-emphasis text-foreground">{runProviders.length + 1}</p>
                          <p className="text-[10px] text-muted-foreground">Providers (incl. AI)</p>
                        </div>
                      </div>

                      {/* Field groups summary */}
                      <div>
                        <p className="text-xs font-emphasis text-muted-foreground uppercase tracking-wider mb-2">
                          Selected fields
                        </p>
                        <div className="space-y-1">
                          {FIELD_CATALOG.filter(g => visibleGroups.includes(g.id)).map(group => {
                            const selected = group.fields.filter(f => selectedFields.has(f.id))
                            if (selected.length === 0) return null
                            return (
                              <div key={group.id} className="flex items-start gap-2 text-xs py-0.5">
                                <span className="text-muted-foreground flex-shrink-0 mt-px">{GROUP_ICONS[group.id]}</span>
                                <span className="font-medium text-foreground flex-shrink-0 w-20">{group.label}</span>
                                <span className="text-muted-foreground flex-1 min-w-0 leading-relaxed">
                                  {selected.map(f => f.label).join(', ')}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      </div>

                      {/* Providers that will run */}
                      <div>
                        <p className="text-xs font-emphasis text-muted-foreground uppercase tracking-wider mb-2">
                          Providers
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          <SourcePill provider="native" />
                          {runProviders.map(p => (
                            <SourcePill key={p.id} provider={p.id} />
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 4: Progress */}
                  {step === 4 && (
                    <motion.div
                      key="step4"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.15 }}
                      className="space-y-5"
                    >
                      <div className="text-center pt-4">
                        {!progressDone && !progressError && (
                          <CircleNotch className="w-8 h-8 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc] mx-auto mb-3" />
                        )}
                        {progressDone && (
                          <CheckCircle className="w-8 h-8 text-emerald-500 dark:text-emerald-400 mx-auto mb-3" />
                        )}
                        {progressError && (
                          <X className="w-8 h-8 text-red-500 mx-auto mb-3" />
                        )}
                        <p className="text-sm font-emphasis text-foreground">
                          {progressError
                            ? 'Enrichment Failed'
                            : progressDone
                              ? (mode === 'bulk' && bulkProgress && bulkProgress.failed > 0
                                  ? 'Enrichment Finished'
                                  : 'Enrichment Complete!')
                              : mode === 'bulk'
                                ? 'Enriching contacts…'
                                : 'Researching...'}
                        </p>
                        {mode === 'bulk' && bulkProgress && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {progressDone
                              ? `${bulkProgress.completed} enriched${bulkProgress.failed ? `, ${bulkProgress.failed} failed` : ''} of ${bulkProgress.total}`
                              : `${bulkProgress.done} of ${bulkProgress.total} processed`}
                          </p>
                        )}
                        {progressError && (
                          <p className="text-xs text-red-400 mt-1">{explainEnrichmentRunError(progressError)}</p>
                        )}
                      </div>

                      {/* Bulk: honest running-in-background box (per-contact SSE
                          steps don't apply to many contacts at once). */}
                      {mode === 'bulk' ? (
                        <div className="border border-border/40 rounded-xl p-4 text-center space-y-2">
                          <p className="text-sm font-medium text-foreground">
                            {bulkProgress ? `${bulkProgress.done} / ${bulkProgress.total} processed` : `0 / ${contactIds.length} processed`}
                          </p>
                          {!progressDone && (
                            <p className="text-xs text-muted-foreground">
                              Enrichment runs in the background — you can close this and keep working; results appear on the contacts list as they finish.
                            </p>
                          )}
                        </div>
                      ) : (
                      /* Single: live SSE step feed */
                      <div className="border border-border/40 rounded-xl p-4 max-h-[280px] overflow-y-auto space-y-1.5">
                        {progressSteps.filter(s => s.step !== 'waiting').map((ps, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <Check className="h-3 w-3 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                            <span className="text-muted-foreground">{ps.label}</span>
                          </div>
                        ))}
                        {loading && (
                          <div className="flex items-center gap-2 text-xs animate-pulse">
                            <CircleNotch className="h-3 w-3 text-[#5e6ad2] dark:text-[#a5b4fc] animate-spin flex-shrink-0" />
                            <span className="text-[#5e6ad2] dark:text-[#a5b4fc] font-medium">
                              {progressSteps.some(s => s.step === 'waiting')
                                ? 'Finalizing research...'
                                : 'Processing...'}
                            </span>
                          </div>
                        )}
                        {progressSteps.length === 0 && loading && (
                          <div className="flex items-center gap-2 text-xs animate-pulse">
                            <CircleNotch className="h-3 w-3 text-[#5e6ad2] dark:text-[#a5b4fc] animate-spin flex-shrink-0" />
                            <span className="text-muted-foreground">Connecting to research stream...</span>
                          </div>
                        )}
                      </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer — navigation */}
              <div className="px-5 py-3 border-t border-border/50 flex items-center justify-between">
                <div>
                  {step > 1 && step < 4 && (
                    <button
                      onClick={() => setStep(s => s - 1)}
                      disabled={loading}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                    >
                      <CaretLeft className="w-4 h-4" />
                      Back
                    </button>
                  )}
                </div>
                <div>
                  {step < 3 && (
                    <button
                      onClick={() => setStep(s => s + 1)}
                      disabled={step === 1 && (!config || !config.has_ai_keys)}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-lg transition-all',
                        config && config.has_ai_keys
                          ? 'bg-[#5e6ad2] text-white hover:bg-[#7170ff] active:bg-[#5058c0]'
                          : 'bg-muted text-muted-foreground cursor-not-allowed'
                      )}
                    >
                      Next
                      <CaretRight className="w-4 h-4" />
                    </button>
                  )}
                  {step === 3 && (
                    <button
                      onClick={handleRun}
                      disabled={loading || selectedFields.size === 0}
                      className={cn(
                        'inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-all',
                        !loading && selectedFields.size > 0
                          ? 'bg-[#5e6ad2] text-white hover:bg-[#5e6ad2]/90 dark:hover:bg-[#7170ff]'
                          : 'bg-muted text-muted-foreground cursor-not-allowed'
                      )}
                    >
                      <Sparkle className="w-4 h-4" />
                      Run Enrichment
                    </button>
                  )}
                  {step === 4 && (progressDone || progressError) && (
                    <button
                      onClick={onClose}
                      className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg bg-[#5e6ad2] text-white hover:bg-[#7170ff] active:bg-[#5058c0] transition-all"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Done
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  ), document.body)
}
